import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ButtonDock, translateTemplateText } from '@focuslee/shared-mini-app';
import { AchievementRarity, CreateAchievementMutation, CreateAchievementMutationVariables, QuestMetricType, UpdateAchievementMutation, UpdateAchievementMutationVariables } from '../client/generated/graphql';
import { createAchievementCache, createAchievementMutation } from '../client/mutations/createAchievement';
import { updateAchievementCache, updateAchievementMutation } from '../client/mutations/updateAchievement';
import { Page } from '../components/Page';
import { AchievementItem } from '../components/achievements/AchievementItem';
import { CategorySelect } from '../components/common/CategorySelect';
import { CommonIcon } from '../components/common/CommonIcon';
import { ConditionPicker } from '../components/common/ConditionPicker';
import { ConditionPickerView, type VoiceLinkedQuestPickerItem } from '../components/common/ConditionPickerView';
import { CustomInput } from '../components/common/CustomInput';
import { CustomSelect } from '../components/common/CustomSelect';
import { IconPicker } from '../components/common/IconPicker';
import { IconPickerSettings } from '../components/common/IconPickerSettings';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { useVoice } from '../contexts/VoiceContext';
import { getAchievementRarityColor, getAchievementRarityColorCode } from '../helpers/achievement';
import { findCategoryByName } from '../helpers/category';
import { getRandomIcon } from '../helpers/common';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedAchievementWithFallback } from '../hooks/caching/achievement';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useAnalytics } from '../hooks/useAnalytics';
import type { CreateAchievementVoiceAction, CreateQuestVoiceAction } from '../types/voice';

export const AchievementCreateEditPage: FC = () => {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const { id: achievementId, categoryId: preselectedCategoryId } = useParams<{ id?: string; categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const voiceActionId = searchParams.get('voiceActionId');
  const { pendingActions, onUpdateVoiceAction } = useVoice();
  const voiceAction = useMemo(
    () => (voiceActionId ? pendingActions.find((a) => a.id === voiceActionId) : null),
    [voiceActionId, pendingActions],
  );
  const voiceBatchParentQuestActions = useMemo((): CreateQuestVoiceAction[] => {
    const ids = voiceAction?.dependsOnActionIds;
    if (!ids?.length) return [];
    return ids
      .map((id) => pendingActions.find((a) => a.id === id))
      .filter((a): a is CreateQuestVoiceAction => a != null && a.type === 'CREATE_QUEST');
  }, [voiceAction?.dependsOnActionIds, pendingActions]);

  const { achievement: editingAchievement } = useCachedAchievementWithFallback(achievementId);
  const { categories: allCategories } = useCachedCategoriesWithFallback();
  const categories = allCategories.filter(c => !c.system);

  const voiceLinkedQuestsForPicker = useMemo((): VoiceLinkedQuestPickerItem[] | undefined => {
    if (!voiceBatchParentQuestActions.length) return undefined;
    return voiceBatchParentQuestActions.map((p) => ({
      voiceActionId: p.id,
      name: p.questName,
      categoryId: p.categoryId,
      categoryName: p.categoryName,
    }));
  }, [voiceBatchParentQuestActions]);

  const [createAchievement, { loading: isCreateLoading }] = useMutation<CreateAchievementMutation, CreateAchievementMutationVariables>(createAchievementMutation, {
    update: createAchievementCache
  });
  const [updateAchievement, { loading: isUpdateLoading }] = useMutation<UpdateAchievementMutation, UpdateAchievementMutationVariables>(updateAchievementMutation);
  
  const [achievementName, setAchievementName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [rarity, setRarity] = useState<string>(AchievementRarity.Uncommon);
  const [icon, setIcon] = useState<string>(getRandomIcon());
  const [conditionMetricType, setConditionMetricType] = useState<QuestMetricType>(QuestMetricType.Repeating);
  const [conditionTarget, setConditionTarget] = useState<number | null>(5);
  const [conditionQuestIds, setConditionQuestIds] = useState<string[]>([]);

  const [showIconSettings, setShowIconSettings] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showConditionPicker, setShowConditionPicker] = useState(false);

  const hasAnyConditionQuest =
    conditionQuestIds.length + (voiceAction?.dependsOnActionIds?.length ?? 0) > 0;
  const isValid = !!categoryId && !!achievementName && !!rarity && hasAnyConditionQuest;
  const isLoading = isCreateLoading || isUpdateLoading;
  const navigate = useNavigate();
  const { recordEvent } = useAnalytics();

  const rarityOptions = useMemo(() => [AchievementRarity.Uncommon, AchievementRarity.Rare, AchievementRarity.Epic, AchievementRarity.Legendary].map(r => ({
    id: r,
    label: t(`achievementEditor.rarities.${r}`),
    color: getAchievementRarityColor(r)
  })), [t]);
  
  useEffect(() => {
    if (nameRef.current && !achievementId && !voiceActionId) {
      nameRef.current.focus();
    }
  }, [achievementId, voiceActionId]);

  useEffect(() => {
    if (voiceActionId) return;
    if (categories && categories.length > 0 && !categoryId) {
      const defaultCategoryId = preselectedCategoryId || categories[0].id;
      setCategoryId(defaultCategoryId);
    } else if (categories && categories.length === 0) {
      setCategoryId('');
    }
  }, [categories, categoryId, preselectedCategoryId, voiceActionId]);

  useEffect(() => {
    if (!voiceActionId || !categories.length || achievementId || !voiceAction) return;
    const va = voiceAction as CreateAchievementVoiceAction;
    setAchievementName(va.achievementName);
    setDescription(va.description ?? '');
    const firstParent = voiceBatchParentQuestActions[0];
    const parentCatId = firstParent
      ? firstParent.categoryId ?? findCategoryByName(categories, firstParent.categoryName ?? '')?.id
      : undefined;
    const catId =
      va.categoryId ??
      findCategoryByName(categories, va.categoryName ?? '')?.id ??
      parentCatId ??
      categories[0]?.id ??
      '';
    setCategoryId(catId);
    const mt = va.conditionMetricType ?? QuestMetricType.Completion;
    setConditionMetricType(mt);
    setConditionTarget(mt === QuestMetricType.Completion ? null : (va.conditionTarget ?? 5));
    setConditionQuestIds(
      (va.dependsOnActionIds?.length ?? 0) > 0 || voiceBatchParentQuestActions.length > 0
        ? (va.conditionQuestIds ?? [])
        : va.conditionQuestIds?.length
          ? va.conditionQuestIds
          : va.questIds?.length
            ? va.questIds
            : [],
    );
    setIcon(va.icon);
    setRarity(va.rarity);
  }, [voiceActionId, voiceAction, voiceBatchParentQuestActions, categories, achievementId]);

  useEffect(() => {
    if (editingAchievement) {
      setAchievementName(translateTemplateText(t, editingAchievement.templateId, 'name', editingAchievement.name));
      setDescription(translateTemplateText(t, editingAchievement.templateId, 'description', editingAchievement.description ?? ''));
      setCategoryId(editingAchievement.categoryId);
      setRarity(editingAchievement.rarity);
      setIcon(editingAchievement.icon || 'star');
      
      if (editingAchievement.achievementConditions && editingAchievement.achievementConditions.length > 0) {
        const condition = editingAchievement.achievementConditions[0];
        setConditionMetricType(condition.metricType);
        setConditionTarget(condition.metricType === QuestMetricType.Completion ? null : (condition.target || 5));
        setConditionQuestIds(condition.questIds || []);
      }
    }
  }, [editingAchievement, t]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement || activeElement.tagName !== 'INPUT') {
        setIsInputFocused(false);
      }
    });
  }, []);

  const handleIconClick = useCallback(() => {
    setShowIconSettings(true);
  }, []);

  const handleSaveIconSettings = useCallback(async (settings: { icon: string }) => {
    setIcon(settings.icon);
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleCancelIconSettings = useCallback(() => {
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleConditionClick = useCallback(() => {
    setShowConditionPicker(true);
  }, []);

  const handleSaveCondition = useCallback(
    (
      metricType: QuestMetricType,
      target: number | null,
      questIds: string[],
      includedVoiceActionIds?: string[],
    ) => {
      setConditionMetricType(metricType);
      setConditionTarget(metricType === QuestMetricType.Completion ? null : target);
      setConditionQuestIds(questIds);
      if (includedVoiceActionIds !== undefined && voiceActionId) {
        onUpdateVoiceAction(voiceActionId, {
          dependsOnActionIds: includedVoiceActionIds.length > 0 ? includedVoiceActionIds : undefined,
        });
      }
      setShowConditionPicker(false);
      setIsInputFocused(false);
    },
    [voiceActionId, onUpdateVoiceAction],
  );

  const handleCancelCondition = useCallback(() => {
    setShowConditionPicker(false);
    setIsInputFocused(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (voiceActionId && voiceAction) {
      const va = voiceAction as CreateAchievementVoiceAction;
      if (!achievementName?.trim() || !categoryId) return;
      const includeLinked = (va.dependsOnActionIds?.length ?? 0) > 0;
      if (!includeLinked && conditionQuestIds.length === 0) return;
      const cat = categories.find((c) => c.id === categoryId);
      onUpdateVoiceAction(va.id, {
        displayTitle: achievementName!,
        achievementName: achievementName!,
        description,
        categoryId,
        categoryName: cat?.name,
        ...(includeLinked
          ? {
              dependsOnActionIds: va.dependsOnActionIds,
              dependsOnActionIndices: va.dependsOnActionIndices,
              questIds: va.questIds,
              questNames: va.questNames,
              conditionQuestIds:
                conditionQuestIds.length > 0 ? [...conditionQuestIds] : [],
            }
          : {
              dependsOnActionIds: undefined,
              dependsOnActionIndices: undefined,
              questIds: conditionQuestIds.length ? [...conditionQuestIds] : undefined,
              questNames: undefined,
              conditionQuestIds: [...conditionQuestIds],
            }),
        icon,
        rarity: rarity as AchievementRarity,
        conditionMetricType,
        conditionTarget: conditionMetricType === QuestMetricType.Completion ? null : conditionTarget,
      });
      navigateBack();
      return;
    }

    if (!isValid || isLoading) return;

    const condition = {
      metricType: conditionMetricType,
      target: conditionTarget,
      questIds: conditionQuestIds,
    };

    const commonVariables = {
      name: achievementName!,
      description: description || '',
      categoryId,
      rarity: rarity as AchievementRarity,
      icon,
      achievementConditions: [condition],
    };

    try {
      if (achievementId) {
        const oldCategoryId = editingAchievement?.categoryId;
        await updateAchievement({ 
          variables: { 
            achievementId, 
            ...commonVariables 
          },
          update: (cache, result) => updateAchievementCache(cache, result, oldCategoryId)
        });
        recordEvent(AnalyticsEventType.ACHIEVEMENT_UPDATED, { achievementId, categoryId });
      } else {
        const result = await createAchievement({ 
          variables: { 
            ...commonVariables 
          } 
        });
        recordEvent(AnalyticsEventType.ACHIEVEMENT_CREATED, { achievementId: result.data?.createAchievement?.id, categoryId });
      }
      navigateBack();
    } catch (error) {
      console.error("Error creating or updating achievement:", error);
    }
  }, [
    isValid,
    isLoading,
    categoryId,
    achievementName,
    description,
    rarity,
    icon,
    conditionMetricType,
    conditionTarget,
    conditionQuestIds,
    achievementId,
    voiceActionId,
    voiceAction,
    categories,
    onUpdateVoiceAction,
    navigateBack,
    editingAchievement,
    updateAchievement,
    createAchievement,
    recordEvent,
  ]);

  const saveButtonText = voiceActionId ? t('achievementEditor.editAction') : achievementId ? t('actions.save') : t('actions.create');

  if (showConditionPicker) {
    return (
      <ConditionPickerView
        initialMetricType={conditionMetricType}
        initialTarget={conditionTarget}
        initialQuestIds={conditionQuestIds}
        voiceLinkedQuests={voiceLinkedQuestsForPicker}
        initialSelectedVoiceActionIds={voiceAction?.dependsOnActionIds ?? []}
        onSave={handleSaveCondition}
        onCancel={handleCancelCondition}
      />
    );
  }

  if (showIconSettings) {
    return (
      <IconPickerSettings
        initialIcon={icon}
        onSave={handleSaveIconSettings}
        onCancel={handleCancelIconSettings}
      />
    );
  }

  return (
    <Page
      back
      insetEnabled={false}
      footer={
        <ButtonDock
          secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
          main={{
            text: saveButtonText,
            onClick: handleSave,
            enabled: isValid && !isLoading,
            loading: isLoading,
            bgColor: getButtonThemeColors(isValid && !isLoading).backgroundColor,
          }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text>{voiceActionId ? t('achievementEditor.editAction') : achievementId ? t('achievementEditor.edit') : t('achievementEditor.create')}</Text>
      </Stack>
      <Stack 
        height="100%" 
        paddingTop="10px" 
        paddingLeft="10px" 
        paddingRight="10px" 
        rowGap="15px"
        sx={{ 
          overflow: isInputFocused ? 'hidden' : 'visible',
          maxHeight: isInputFocused ? '70vh' : 'none'
        }}
      >
        {!isInputFocused && (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            <Box sx={{
              width: 100,
              height: 140,
            }}>
              <AchievementItem
                icon={icon}
                iconImageUrl={editingAchievement?.iconImageUrl}
                iconImageColor={editingAchievement?.iconImageColor}
                title={achievementName || t('achievementEditor.name')}
                rarity={rarity as AchievementRarity}
              />
            </Box>
          </Stack>
        )}

        <CustomInput
          value={achievementName}
          onChange={(e) => setAchievementName(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={nameRef}
          style={{ fontSize: '20px', lineHeight: '20px' }}
          wrapperStyle={{ minHeight: '20px' }}
          placeholder={t('achievementEditor.name')}
        />

        <CustomInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={t('achievementEditor.description')}
          wrapperStyle={{ minHeight: '15px' }}
          ref={descriptionRef}
        />

        <>
          <Stack flexWrap="wrap" direction="row" justifyContent="flex-start" columnGap="10px" rowGap="20px">
            <CategorySelect 
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              categories={categories}
            />
            
            <CustomSelect 
              selectedId={rarity}
              setSelectedId={setRarity}
              options={rarityOptions}
              text={(r) => r.label}
              renderSelected={(r) => r ? r.label : t('achievementEditor.rarity')}
              renderSelectedBefore={() => <CommonIcon icon="diamond" type="filled" color="var(--tg-theme-text-color)" size={20} />}
            />
          </Stack>

          <IconPicker
            selectedIcon={icon}
            selectedIconColor={getAchievementRarityColorCode(rarity as AchievementRarity)}
            onClick={handleIconClick}
          />

          <ConditionPicker
            metricType={conditionMetricType}
            target={conditionTarget}
            questIds={conditionQuestIds}
            linkedNewQuestNames={
              voiceBatchParentQuestActions.length > 0
                ? voiceBatchParentQuestActions.map((p) => p.questName)
                : undefined
            }
            onClick={handleConditionClick}
          />
        </>
      </Stack>
    </Page>
  );
};
